// Garder le lien avec les partenaires (D-125, module partenaires), d'après les maquettes validées :
//   #/lien             le bureau : « Ce mois-ci » (ce que l'app propose), « La saison » des attentions (g4)
//   « Ce que le club offre » : une rubrique des réglages des partenaires (D-139 ; #/lien/offres y mène), la liste du
//                      club, aucune attention imposée (off1)
//   la fiche d'un partenaire : « Garder le lien », dernier geste, l'attention proposée, le fil (g1, partner-page.ts)
//   la fiche « Inviter … » : le match, qui l'accueille, ce que le club offre, le merci au micro, comment ça part,
//                      l'aperçu de l'e-mail (off2 ; D-147 a1)
//   #/lien (partenaire) : « Le club vous invite », Je viens / Je ne peux pas, le bon du soir du match (g3 ; D-147 n1)
//   l'onglet Direct d'un match : le merci au micro pour la table de marque, la photo avec la pancarte
// L'app propose et prépare ; rien ne part sans le bureau. D-147 : toutes les communications aux partenaires (invitation,
// relance, nouvelles, vœux, merci, fin de saison) partent par le même circuit : l'e-mail HTML du club, mis en file par
// la base et envoyé par push-dispatch (« <Club> via Arceau », réponses au membre du bureau qui envoie), et la
// notification de son espace s'il a un compte. Plus de mailto ni de copie cachée ; sans e-mail ni compte, la page de
// réponse se partage par WhatsApp ou SMS. Rien d'argent dans ce fil. Mineurs (D-103) : rien de tout cela ne leur est montré.
//
// Module (D-122) : tous les points d'entrée passent par lienOn() ; à la fusion avec feat/modules, il devient
// « modOn(ctx, 'partenaires') » (une ligne).
import './lien.css';
import {
  lienBoard, lienBonUse, lienFollowup, lienGiftAdd, lienGiftPreset, lienGiftRemove, lienGiftSet, lienInviteCancel, lienInviteMark, lienInviteSend,
  lienMailPref, lienMatch, lienMessageSend, lienMyMail, lienNewsInputs, lienNewsSave, lienNewsSend, lienNote, lienPhoto, lienPhotoAdd, lienReply,
  lienSeen, lienSpace, lienThread, type MailReach, type Space, type SpaceInvitation, type ThreadItem,
} from '../lib/lien-api';
import {
  STATUS_TEXT, andList, bonTitle, composeNews, dayMonth, giftRows, hasMoney, hostLabel, inviteMessage, inviteShareText, lastGestureText, longDay,
  micOn, monthItems, monthTodo, newsSummary, offerable, proposalFor, proposalLine, quarterOf, reachCount, seasonState, shortDay,
  shortOf, waysOf, type Board, type BoardMatch, type BoardPartner, type FollowInvite, type Gift, type MonthItem, type PlanState,
} from '../domain/lien';
import { firstName, modOn, type Ctx } from './context';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import { followCard, loadReach, miniMail, openFullMail, previewMail, shareAnswerLink, waysCards, waysCount, wireFollow } from './lien-mail';
import { openUseHold } from './perks';
import { partnersHead } from './partners';
import { photoToJpeg } from './match-extras';

/** Le module partenaires est-il là ? (D-122.) */
export const lienOn = (ctx: Ctx): boolean => modOn(ctx, 'partenaires');
const isBureau = (ctx: Ctx) => !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);
const ico = (i: string): Html => html`<span class="ln-ico" aria-hidden="true"><span class="ti ${i}"></span></span>`;
const upperFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;
const jpeg = (data: string) => `data:image/jpeg;base64,${data}`;

// =============================================================================================
// Le bureau : « Garder le lien » (g4)
// =============================================================================================
export async function renderLien(root: HTMLElement, ctx: Ctx, sub?: string): Promise<void> {
  const club = ctx.club;
  if (!club || !lienOn(ctx) || !isBureau(ctx)) {
    mount(root, html`<div class="empty"><p class="empty-title">Réservé au bureau.</p></div>`);
    return;
  }
  const b = await lienBoard(club.id);
  if (!b) { mount(root, html`<div class="empty"><p class="empty-title">Chargement impossible.</p></div>`); return; }
  const items = monthItems(b);
  const todo = monthTodo(b).length;   // le compteur : ce qui demande un geste (B4), pas les invitations envoyées
  const season = seasonState(b);
  const reload = () => renderLien(root, ctx);
  const n = b.partners.length;
  const active = b.gifts.filter(g => g.active);

  mount(root, html`
    <section class="screen">
      ${partnersHead(ctx, 'lien')}
      <section class="block" data-guide="ln-month"><div class="block-head"><h3>Ce mois-ci</h3>${todo ? html`<span class="chip chip-accent">${todo}</span>` : ''}</div>
        ${items.length ? html`<ul class="ln-list">${items.map((it, k) => itemRow(it, k, b))}</ul>`
          : html`<p class="muted small">${n ? 'Rien à faire ce mois-ci : chaque partenaire a eu son attention.' : 'Ajoute d’abord tes partenaires (Club › Partenaires).'}</p>`}
      </section>
      <section class="block" data-guide="ln-season"><div class="block-head"><h3>La saison</h3></div>
        <ul class="ln-cal">${season.map(planRow)}</ul></section>
      <a class="card card-row" href="#/partenaires/reglages/offres" data-guide="ln-offres">${ico('ti-gift')}
        <span class="card-main"><strong>Ce que le club offre</strong><span class="muted small">${active.length
          ? `${active.length > 3 ? `${active.slice(0, 3).map(g => shortOf(g)).join(', ')}…` : andList(active.map(g => shortOf(g)))} · ${plural(active.length, 'attention', 'attentions')}`
          : 'Rien d’imposé : compose ta liste'}</span></span><span class="occ-go" aria-hidden="true">›</span></a>
      ${n ? html`<button type="button" class="btn btn-ghost" id="ln-moment">＋ Un grand moment à partager</button>` : ''}
      <p class="hint">Un bon moment par trimestre pour chaque partenaire, sans jamais lui demander d’argent : l’app propose, tu valides en un geste. Rien d’argent ici : ni montant, ni contrat, ni relance.</p>
    </section>`);

  for (const el of $$<HTMLElement>(root, '[data-item]')) {
    el.addEventListener('click', () => {
      const it = items[Number(el.dataset.item)];
      if (!it) return;
      if (it.kind === 'invite') void openInviteSheet(ctx, b, it.partner, it.match.event_id, reload);
      else if (it.kind === 'invited') location.hash = `#/partenaires/${it.partner.id}`;
      else if (it.kind === 'news') void openNewsSheet(ctx, b, reload);
      else if (it.kind === 'voeux') void openMessageSheet(ctx, b, 'voeux', reload);
      else if (it.kind === 'merci') void openMessageSheet(ctx, b, 'merci', reload, it.moment);
      else void openMessageSheet(ctx, b, 'fin_saison', reload);
    });
  }
  $(root, '#ln-moment')?.addEventListener('click', () => void openMessageSheet(ctx, b, 'merci', reload));
}

function itemRow(it: MonthItem, k: number, b: Board): Html {
  const row = (icon: string, title: string, sub: string, guide = '') => html`
    <li><button type="button" class="card card-row ln-item" data-item="${k}" ${guide ? html`data-guide="${guide}"` : ''}>${ico(icon)}
      <span class="card-main"><strong>${title}</strong><span class="muted small">${sub}</span></span><span class="occ-go" aria-hidden="true">›</span></button></li>`;
  const aux = `aux ${plural(b.partners.length, 'partenaire', 'partenaires')}`;
  switch (it.kind) {
    case 'invite': return row('ti-flag', `Inviter ${it.partner.name} au match`,
      [it.match.title, shortDay(it.match.day), it.last ? lastGestureText(it.last, b.today) : 'aucun geste encore'].join(' · '), k === 0 ? 'ln-invite' : '');
    case 'invited': return row('ti-flag', `${it.partner.name} · ${it.invitation.status === 'envoyee' ? 'invitation envoyée' : it.invitation.status === 'vient' ? 'vient au match' : 'pas cette fois'}`,
      [it.invitation.title, shortDay(it.invitation.day), it.invitation.status === 'envoyee' ? STATUS_TEXT.envoyee : it.invitation.host_name ? `accueil : ${it.invitation.host_name}` : ''].filter(Boolean).join(' · '));
    case 'news': return row('ti-megaphone', it.draft?.title ?? `Les nouvelles ${it.quarter.name}`,
      `${it.draft ? `Prêtes à relire : ${newsSummary(it.draft.body, it.draft.photo_ids.length) || 'le texte du trimestre'}` : 'À composer : résultats, temps forts, photos'} · ${aux}`);
    case 'voeux': return row('ti-heart', 'La carte de vœux des équipes', `Un mot du club pour la nouvelle année · ${aux}`);
    case 'merci': return row('ti-trophy', `Un merci après la victoire contre ${it.moment.title.split(' – ').slice(-1)[0]}`, `${it.moment.title} · ${it.moment.us}–${it.moment.them} · ${aux}`);
    case 'fin': return row('ti-heart', 'La fin de saison', `Photo d’équipe signée, fête du club · ${aux}`);
  }
}

function planRow(s: PlanState): Html {
  const chip = s.row.kind === 'invite' && s.state === 'en cours' ? html`<span class="chip chip-ok">${s.done} / ${s.total}</span>`
    : s.state === 'fait' ? html`<span class="chip chip-ok">${s.row.kind === 'news' ? 'publiées' : 'fait'}</span>`
    : s.state === 'prêtes' ? html`<span class="chip chip-mute">prêtes</span>`
    : s.state === 'à préparer' ? html`<span class="chip chip-accent">à préparer</span>`
    : html`<span class="chip chip-mute">${s.state}</span>`;
  return html`<li><span class="m">${s.row.months}</span><span>${s.row.title}</span>${chip}</li>`;
}

// =============================================================================================
// Ce que le club offre (off1)
// =============================================================================================
/** Réglages · Partenaires, rubrique « Ce que le club offre » (D-139 : c'était une page de Garder le lien). */
export function renderGiftsAdmin(root: HTMLElement, ctx: Ctx, gifts: Gift[], again: () => void): void {
  const club = ctx.club!;
  const rows = giftRows(gifts);
  mount(root, html`
      <p class="muted small nv-intro">C’est le club qui choisit ce qu’il offre à ses partenaires. Rien n’est imposé : compose ta liste, puis choisis à chaque invitation.</p>
      <ul class="ln-list" data-guide="ln-gifts-list">${rows.map((r, k) => html`
        <li class="card card-row">${ico('ti-gift')}
          <span class="card-main"><strong>${r.title}</strong><span class="muted small">${r.sub}</span>
            ${r.id && !r.preset ? html`<button type="button" class="ln-link" data-remove="${r.id}">Retirer</button>` : ''}</span>
          <button type="button" class="iv-switch" role="switch" aria-checked="${String(r.active)}" aria-label="${r.title}" data-row="${k}"></button></li>`)}</ul>
      <button type="button" class="btn btn-ghost btn-sm" id="ln-add" data-guide="ln-add">＋ Ajouter une attention</button>
      <p class="hint">Une attention offerte le soir d’un match devient un bon à montrer à la buvette (appui de 5 s, comme les avantages).</p>`);
  for (const sw of $$<HTMLButtonElement>(root, '[data-row]')) {
    sw.addEventListener('click', async () => {
      const r = rows[Number(sw.dataset.row)]!;
      const on = sw.getAttribute('aria-checked') !== 'true';
      sw.disabled = true;
      try {
        if (r.preset) await lienGiftPreset(club.id, r.preset, on); else await lienGiftSet(r.id!, on);
        sw.setAttribute('aria-checked', String(on));
        toast(on ? `${r.title} : dans la liste du club` : `${r.title} : retiré de la liste`);
      } catch { toast('Impossible pour le moment.', 'ko'); }
      sw.disabled = false;
    });
  }
  for (const x of $$<HTMLButtonElement>(root, '[data-remove]')) {
    x.addEventListener('click', async () => {
      if (!armed(x, 'Retirer ?')) return;
      try { await lienGiftRemove(x.dataset.remove!); toast('Attention retirée'); again(); } catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
  $(root, '#ln-add')!.addEventListener('click', () => openSheet('Une attention du club', html`
    <form class="form" id="ln-gf" novalidate>
      <label class="field"><span>Ce que le club offre</span><input name="title" maxlength="60" required placeholder="Écharpe du club, programme dédicacé…"></label>
      <label class="check"><input type="checkbox" name="bon"><span><strong>Elle devient un bon le soir du match</strong><br><span class="muted small">À montrer à la buvette ou au stand, avec l’appui de 5 s</span></span></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Ajouter à la liste</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#ln-gf')!;
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const title = String(d.get('title') ?? '').trim();
      const msg = $(f, '.form-msg')!;
      if (!title) { msg.textContent = 'Donne un nom à l’attention.'; return; }
      if (hasMoney(title)) { msg.textContent = 'Rien d’argent ici : une attention, pas un montant.'; return; }
      try { await lienGiftAdd(club.id, title, d.get('bon') === 'on'); close(); toast('Attention ajoutée à la liste'); again(); }
      catch { msg.textContent = 'Ajout impossible.'; }
    });
  }));
}

// =============================================================================================
// Inviter au match (off2)
// =============================================================================================
/** « Dim. 8 nov. · 15h30 · SF2 – BC Tavernel » (maquette a1). */
const matchOption = (m: BoardMatch) => `${upperFirst(shortDay(m.day))} · ${m.time} · ${m.title}`;
const EMAIL = /^[^@\s<>,;"()]+@[^@\s<>,;"()]+\.[^@\s<>,;"()]+$/;

/** La fiche « Inviter … » : le match, qui l'accueille, ce que le club offre, le merci au micro ; comment ça part (l'e-mail du
 *  club, la notification s'il a un compte) et l'aperçu de ce qu'il reçoit (D-147, maquette a1). */
export async function openInviteSheet(ctx: Ctx, b: Board, p: BoardPartner, eventId: string | null, onDone: () => void): Promise<void> {
  const club = ctx.club!;
  const matches = b.matches.filter(m => m.day >= b.today);
  if (!matches.length) { toast('Pas de match à domicile dans les deux mois.', 'ko'); return; }
  const reach = await loadReach(ctx);
  const rp = reach?.partners.find(x => x.id === p.id) ?? null;
  const ways = waysOf(rp ?? { id: p.id, name: p.name, email: null, has_account: p.has_account, app_only: false, stopped: false, to: null });
  let match = matches.find(m => m.event_id === eventId) ?? matches[0]!;
  const gifts = offerable(b.gifts);
  const mic = micOn(b.gifts) && !p.restricted;
  const me = b.hosts.find(h => h.user_id === ctx.uid);
  let host = me ?? b.hosts[0] ?? null;
  // Les bons du club, cochés d'office (comme la maquette) ; tout se décoche, « Rien cette fois » aussi.
  const picked = new Set(gifts.filter(g => g.is_bon || g.kind === 'place').map(g => g.id));
  const blocked = (g: Gift) => (p.restricted && (g.kind === 'presente' || g.kind === 'photo'))
    || (g.kind === 'presente' && !!match.presented_by && match.presented_by !== p.id);
  const who = p.contact_name || p.name;
  const choice = (useMic: boolean) => ({
    partnerName: p.name, civility: p.civility, contact: p.contact_name, match: { title: match.title, day: match.day, time: match.time, location: match.location },
    host: host?.name ?? null, gifts: gifts.filter(g => picked.has(g.id) && !blocked(g)).map(g => ({ title: g.title, kind: g.kind, preset: g.preset, is_bon: g.is_bon })), mic: useMic,
  });
  openSheet(`Inviter ${p.name}`, html`
    <div class="form">
      <label class="field"><span>Le match</span><select id="ln-m">${matches.map(m => html`<option value="${m.event_id}" ${m.event_id === match.event_id ? 'selected' : ''}>${matchOption(m)}</option>`)}</select></label>
      <label class="field"><span>${hostLabel(p.civility)}</span><select id="ln-h"><option value="">Personne en particulier</option>
        ${b.hosts.map(h => html`<option value="${h.user_id}" ${h.user_id === host?.user_id ? 'selected' : ''}>${h.name}${h.user_id === ctx.uid ? ' (moi)' : ''}</option>`)}</select></label>
      <div class="field" data-guide="ln-gifts"><span>Ce que le club offre</span>
        ${gifts.length ? html`<div class="chip-toggles" id="ln-g">${gifts.map(g => html`
          <button type="button" class="chip-toggle" data-g="${g.id}" aria-pressed="${String(picked.has(g.id))}">${g.title}</button>`)}
          <button type="button" class="chip-toggle" data-none aria-pressed="false">Rien cette fois</button></div>
          <p class="hint" id="ln-gh" hidden></p>`
          : html`<p class="muted small">La liste du club est vide : <a href="#/lien/offres" data-close>compose-la</a>, ou invite sans rien offrir.</p>`}</div>
      ${mic ? html`<label class="check" data-guide="ln-mic"><input type="checkbox" id="ln-mic" checked><span><strong>Un merci au micro</strong><br><span class="muted small">Une ligne pour la table de marque, à lire à la mi-temps</span></span></label>` : ''}
      ${p.restricted ? html`<p class="hint">Alcool ou jeux d’argent (loi Évin) : ni merci au micro, ni match présenté, ni pancarte. L’invitation reste possible.</p>` : ''}
      <div class="field" data-guide="ln-ways"><span>Comment ça part</span><div id="pm-ways">${waysCards({ ...ways, clubName: club.name, addEmail: true })}</div></div>
      <div class="field" data-guide="ln-message"><span>${who} reçoit</span><div id="pm-prev"></div></div>
      <button type="button" class="btn btn-primary" id="ln-send" data-guide="ln-send">Envoyer l’invitation</button>
      ${!ways.email && !ways.app ? html`<button type="button" class="btn btn-ghost" id="ln-wa">Par WhatsApp ou SMS</button>` : ''}
      <p class="hint" id="ln-send-hint"></p>
      <p class="form-msg" role="alert"></p>
    </div>`, (sheet, close) => {
    const micBox = () => !!$<HTMLInputElement>(sheet, '#ln-mic')?.checked;
    const typed = () => { const v = ($<HTMLInputElement>(sheet, '#pm-email')?.value ?? '').trim().toLowerCase(); return EMAIL.test(v) ? v : null; };
    const to = () => ways.email ?? typed();
    const build = () => previewMail(ctx, reach, { kind: 'invitation', partner: { name: p.name, civility: p.civility, contact: p.contact_name },
      invitation: { token: '0'.repeat(64), title: match.title, day: match.day, time: match.time, location: match.location, competition: 'championnat',
        host: host?.name ?? null, gifts: choice(false).gifts, mic: micBox() } });
    const paint = () => {
      const none = $(sheet, '[data-none]');
      for (const x of $$<HTMLButtonElement>(sheet, '[data-g]')) {
        const g = gifts.find(y => y.id === x.dataset.g)!;
        x.disabled = blocked(g);
        x.setAttribute('aria-pressed', String(picked.has(g.id) && !blocked(g)));
      }
      none?.setAttribute('aria-pressed', String(![...picked].some(id => !blocked(gifts.find(y => y.id === id)!))));
      const gh = $(sheet, '#ln-gh');
      const taken = gifts.find(g => g.kind === 'presente' && blocked(g) && !p.restricted);
      if (gh) { gh.hidden = !taken; gh.textContent = taken ? `Ce match est déjà présenté par ${match.presented_name ?? 'un autre partenaire'}.` : ''; }
      const mail = !!to();
      $(sheet, '[data-way="mail"]')?.classList.toggle('on', mail);
      $(sheet, '[data-way="mail"]')?.classList.toggle('off', !mail);
      if (!ways.email && typed()) $(sheet, '#pm-mail-sub')!.textContent = `À ${typed()} · « ${club.name} via Arceau », les réponses arrivent chez toi`;
      mount($(sheet, '#pm-prev')!, mail || ways.app ? miniMail(build().inner) : html`<p class="muted small">Sans e-mail ni compte partenaire : ajoute son e-mail, ou envoie-lui le lien de la page de réponse par WhatsApp ou SMS.</p>`);
      $(sheet, '[data-full]')?.addEventListener('click', () => openFullMail(build(), to(), reach?.me.name ?? null));
      const sendBtn = $<HTMLButtonElement>(sheet, '#ln-send')!;
      sendBtn.disabled = !mail && !ways.app;
      $(sheet, '#ln-send-hint')!.textContent = mail
        ? `${p.civility === 'Monsieur' ? 'Il' : p.civility === 'Madame' ? 'Elle' : 'Il'} répond en un clic, sans compte : la réponse arrive sur sa fiche et sur celle du match. Sans réponse au bout de 3 jours, l’app te propose une relance.`
        : ways.app ? 'Une notification dans son espace : la réponse arrive sur sa fiche et sur celle du match. Sans réponse au bout de 3 jours, l’app te propose une relance.'
        : '';
    };
    $<HTMLSelectElement>(sheet, '#ln-m')!.addEventListener('change', e => { match = matches.find(m => m.event_id === (e.target as HTMLSelectElement).value) ?? match; paint(); });
    $<HTMLSelectElement>(sheet, '#ln-h')!.addEventListener('change', e => { host = b.hosts.find(h => h.user_id === (e.target as HTMLSelectElement).value) ?? null; paint(); });
    for (const x of $$<HTMLButtonElement>(sheet, '[data-g]')) x.addEventListener('click', () => {
      const id = x.dataset.g!;
      if (picked.has(id)) picked.delete(id); else picked.add(id);
      paint();
    });
    $(sheet, '[data-none]')?.addEventListener('click', () => { picked.clear(); paint(); });
    $(sheet, '#ln-mic')?.addEventListener('change', paint);
    $(sheet, '#pm-email')?.addEventListener('input', paint);
    paint();
    const err = (t: string) => { $(sheet, '.form-msg')!.textContent = t; };
    const send = (link: boolean) => lienInviteSend({ partner: p.id, event: match.event_id, host: host?.user_id ?? null, mic: micBox(),
      gifts: gifts.filter(g => picked.has(g.id) && !blocked(g)).map(g => g.id), email: ways.email ? null : typed(), link });
    const fail = (e: unknown) => {
      const m = String((e as Error).message ?? '');
      err(m.includes('déjà présenté') ? 'Ce match est déjà présenté par un autre partenaire.' : m.includes('unique') || m.includes('duplicate')
        ? `${p.name} est déjà invité à ce match.` : m.includes('e-mail invalide') ? 'Cette adresse e-mail n’est pas valable.'
        : m.includes('trop d') ? 'Trop d’e-mails aux partenaires aujourd’hui : réessaie demain.' : 'Envoi impossible pour le moment.');
    };
    $(sheet, '#ln-send')!.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        const r = await send(false);
        close();
        toast(`Invitation envoyée à ${p.name} : ${r.via === 'email' ? (r.has_account ? 'un e-mail et une notification' : 'par e-mail') : 'dans son espace'}`);
        onDone();
      } catch (x) { btn.disabled = false; fail(x); }
    });
    $(sheet, '#ln-wa')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        const r = await send(true);
        close();
        shareAnswerLink(p.name, inviteShareText(choice(micBox()), `${location.origin}/l/${r.token}`));
        onDone();
      } catch (x) { btn.disabled = false; fail(x); }
    });
  });
}

// =============================================================================================
// Les nouvelles du trimestre : un brouillon composé tout seul, relu par le bureau
// =============================================================================================
async function openNewsSheet(ctx: Ctx, b: Board, onDone: () => void): Promise<void> {
  const club = ctx.club!;
  const q = quarterOf(b.today);
  const stored = b.news.find(n => n.quarter === q.key && n.status === 'brouillon') ?? null;
  const inputs = await lienNewsInputs(club.id, q.from, q.to).catch(() => null);
  const composed = inputs ? composeNews(inputs, q, club.name) : null;
  const title = stored?.title ?? composed?.title ?? `Les nouvelles ${q.name}`;
  const body = stored?.body ?? composed?.body ?? '';
  const photos = inputs?.photos ?? [];
  const chosen = new Set(stored?.photo_ids ?? composed?.photoIds ?? []);
  const reach = await loadReach(ctx);
  const rc = reachCount(reach?.partners ?? []);
  openSheet(title, html`
    <div class="form">
      <label class="field"><span>Titre</span><input id="ln-nt" maxlength="80" value="${title}"></label>
      <label class="field"><span>Le texte</span><textarea id="ln-nb" rows="12" maxlength="3000">${body}</textarea></label>
      ${photos.length ? html`<div class="field"><span>Deux photos déjà publiées sur la page du club</span>
        <div class="ln-photos">${photos.map(ph => html`<button type="button" class="ln-ph" data-ph="${ph.id}" aria-pressed="${String(chosen.has(ph.id))}" aria-label="${ph.match}"><img alt="" data-src="${ph.id}"></button>`)}</div></div>`
        : html`<p class="hint">Pas encore de photo publiée (avec le droit à l’image) : les nouvelles partent sans photo.</p>`}
      <p class="hint">Composé tout seul : les résultats du trimestre, les belles victoires, ce qui arrive. Relis, corrige, puis publie.</p>
      <div class="field"><span>Comment ça part</span>${waysCount({ mail: rc.mail, app: rc.app.map(x => x.name), without: rc.without.map(x => x.name), clubName: club.name })}</div>
      <div class="field"><span>Ils reçoivent</span><div id="pm-prev"></div></div>
      <button type="button" class="btn btn-primary" id="ln-pub">Publier et envoyer</button>
      <button type="button" class="btn btn-ghost" id="ln-keep">Garder le brouillon</button>
      <p class="hint">Le même circuit que les invitations, les mercis et la fin de saison : l’e-mail du club, et la notification dans l’espace de ceux qui ont un compte.</p>
      <p class="form-msg" role="alert"></p>
    </div>`, (sheet, close) => {
    const prev = () => {
      const t = $<HTMLInputElement>(sheet, '#ln-nt')!.value.trim() || title, x = $<HTMLTextAreaElement>(sheet, '#ln-nb')!.value.trim() || body;
      const m = previewMail(ctx, reach, { kind: 'nouvelles', partner: { name: '', civility: null, contact: null }, news: { title: t, body: x } });
      mount($(sheet, '#pm-prev')!, miniMail(m.inner));
      $(sheet, '[data-full]')?.addEventListener('click', () => openFullMail(m, null, reach?.me.name ?? null));
    };
    prev();
    $(sheet, '#ln-nt')!.addEventListener('change', prev);
    $(sheet, '#ln-nb')!.addEventListener('change', prev);
    for (const img of $$<HTMLImageElement>(sheet, 'img[data-src]')) void lienPhoto(img.dataset.src!).then(d => { if (d) img.src = jpeg(d); }).catch(() => undefined);
    for (const x of $$<HTMLButtonElement>(sheet, '[data-ph]')) x.addEventListener('click', () => {
      const id = x.dataset.ph!;
      if (chosen.has(id)) chosen.delete(id); else if (chosen.size < 2) chosen.add(id); else { toast('Deux photos au plus.', 'ko'); return; }
      x.setAttribute('aria-pressed', String(chosen.has(id)));
    });
    const err = (t: string) => { $(sheet, '.form-msg')!.textContent = t; };
    const save = async () => {
      const t = $<HTMLInputElement>(sheet, '#ln-nt')!.value.trim(), x = $<HTMLTextAreaElement>(sheet, '#ln-nb')!.value.trim();
      if (!t || !x) { err('Un titre et un texte.'); return null; }
      if (hasMoney(`${t} ${x}`)) { err('Rien d’argent dans les nouvelles : retire le montant.'); return null; }
      return lienNewsSave(club.id, q.key, t, x, [...chosen]);
    };
    $(sheet, '#ln-keep')!.addEventListener('click', async () => {
      try { if (await save()) { close(); toast('Brouillon gardé'); onDone(); } } catch { err('Enregistrement impossible.'); }
    });
    $(sheet, '#ln-pub')!.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        const id = await save();
        if (!id) { btn.disabled = false; return; }
        const r = await lienNewsSend(id);
        toast(`Publiées : ${plural(r.mailed, 'e-mail', 'e-mails')}${r.notified ? `, ${plural(r.notified, 'espace partenaire', 'espaces partenaires')}` : ''}`);
        close(); onDone();
      } catch (e) { btn.disabled = false; err(String((e as Error).message).includes('trop d') ? 'Trop d’e-mails aux partenaires aujourd’hui : réessaie demain.' : 'Publication impossible.'); }
    });
  });
}

// =============================================================================================
// Vœux, merci après un grand moment, fin de saison : un mot du club à tous
// =============================================================================================
type MsgKind = 'voeux' | 'merci' | 'fin_saison';
function messagePreset(kind: MsgKind, clubName: string, today: string, moment?: Board['moments'][number]): { title: string; detail: string } {
  const year = Number(today.slice(0, 4));
  if (kind === 'voeux') return { title: `Belle année ${year} !`, detail: `Toute l’équipe du ${clubName}, joueurs, joueuses, coachs et bénévoles, vous souhaite une belle année ${year}. Merci d’être à nos côtés !` };
  if (kind === 'fin_saison') return { title: 'Merci pour cette saison', detail: `La saison se termine : merci d’avoir été à nos côtés. Une photo d’équipe signée vous attend, et vous êtes les bienvenus à la fête du club.` };
  if (moment) {
    const opp = moment.title.split(' – ').slice(-1)[0];
    return { title: `Merci après la victoire contre ${opp}`, detail: `${moment.title} : ${moment.us}–${moment.them}. Merci d’être à nos côtés, cette victoire est aussi un peu la vôtre.` };
  }
  return { title: 'Un grand merci', detail: '' };
}

export async function openMessageSheet(ctx: Ctx, b: Board, kind: MsgKind, onDone: () => void, moment?: Board['moments'][number]): Promise<void> {
  const club = ctx.club!;
  const reach = await loadReach(ctx);
  const pre = messagePreset(kind, club.name, b.today, moment);
  const chosen = new Set(b.partners.map(p => p.id));
  const head = kind === 'voeux' ? 'La carte de vœux' : kind === 'fin_saison' ? 'La fin de saison' : 'Un merci du club';
  openSheet(head, html`
    <div class="form">
      ${kind === 'merci' && !moment ? html`<p class="hint">Une montée, un titre, une belle victoire : dis-le aux partenaires, sans rien leur demander.</p>` : ''}
      <label class="field"><span>Titre</span><input id="ln-mt" maxlength="120" value="${pre.title}" placeholder="Merci après la montée"></label>
      <label class="field"><span>Le mot du club</span><textarea id="ln-md" rows="4" maxlength="500" placeholder="Les SF1 montent en Régionale 2 : merci d’avoir été là.">${pre.detail}</textarea></label>
      <div class="field"><span>À qui</span><div class="chip-toggles">${b.partners.map(p => html`
        <button type="button" class="chip-toggle" data-p="${p.id}" aria-pressed="true">${p.name}</button>`)}</div></div>
      <div class="field"><span>Comment ça part</span><div id="pm-ways"></div></div>
      <div class="field"><span>Ils reçoivent</span><div id="pm-prev"></div></div>
      <button type="button" class="btn btn-primary" id="ln-ms">Envoyer</button>
      <p class="hint">Le même circuit que les invitations et les nouvelles : l’e-mail du club, et la notification dans l’espace de ceux qui ont un compte.</p>
      <p class="form-msg" role="alert"></p>
    </div>`, (sheet, close) => {
    const btn = $<HTMLButtonElement>(sheet, '#ln-ms')!;
    const label = () => {
      btn.textContent = `Envoyer à ${plural(chosen.size, 'partenaire', 'partenaires')}`; btn.disabled = !chosen.size;
      const rc = reachCount((reach?.partners ?? []).filter(x => chosen.has(x.id)));
      mount($(sheet, '#pm-ways')!, waysCount({ mail: rc.mail, app: rc.app.map(x => x.name), without: rc.without.map(x => x.name), clubName: club.name }));
      const t = $<HTMLInputElement>(sheet, '#ln-mt')!.value.trim() || pre.title, d = $<HTMLTextAreaElement>(sheet, '#ln-md')!.value.trim();
      const m = previewMail(ctx, reach, { kind, partner: { name: '', civility: null, contact: null }, message: { title: t, detail: d || null } });
      mount($(sheet, '#pm-prev')!, miniMail(m.inner));
      $(sheet, '[data-full]')?.addEventListener('click', () => openFullMail(m, null, reach?.me.name ?? null));
    };
    label();
    $(sheet, '#ln-mt')!.addEventListener('change', label);
    $(sheet, '#ln-md')!.addEventListener('change', label);
    for (const x of $$<HTMLButtonElement>(sheet, '[data-p]')) x.addEventListener('click', () => {
      const id = x.dataset.p!;
      if (chosen.has(id)) chosen.delete(id); else chosen.add(id);
      x.setAttribute('aria-pressed', String(chosen.has(id)));
      label();
    });
    btn.addEventListener('click', async () => {
      const t = $<HTMLInputElement>(sheet, '#ln-mt')!.value.trim(), d = $<HTMLTextAreaElement>(sheet, '#ln-md')!.value.trim();
      const msg = $(sheet, '.form-msg')!;
      if (!t) { msg.textContent = 'Un titre.'; return; }
      if (hasMoney(`${t} ${d}`)) { msg.textContent = 'Rien d’argent ici : un merci, pas une demande.'; return; }
      btn.disabled = true;
      try {
        const r = await lienMessageSend(club.id, kind, t, d, [...chosen], null);
        toast(`Envoyé à ${plural(r.sent, 'partenaire', 'partenaires')}${r.mailed ? ` · ${plural(r.mailed, 'e-mail', 'e-mails')}` : ''}`);
        close(); onDone();
      } catch (e) { btn.disabled = false; msg.textContent = String((e as Error).message).includes('trop d') ? 'Trop d’e-mails aux partenaires aujourd’hui : réessaie demain.' : 'Envoi impossible.'; }
    });
  });
}

// =============================================================================================
// La fiche d'un partenaire : la section « Garder le lien » (g1)
// =============================================================================================
const threadDetail = (t: ThreadItem): string => {
  const extra = t.kind === 'invitation' && t.invitation_status ? STATUS_TEXT[t.invitation_status]
    : t.kind === 'nouvelles' && t.seen ? 'lues' : t.shared && t.seen && t.kind !== 'invitation' ? 'lu' : '';
  return [t.detail ?? '', extra].filter(Boolean).join(' · ');
};

/** « Garder le lien » sur la fiche du partenaire : dernier geste, l'attention proposée maintenant, le fil (rien d'argent). */
export async function renderLienSection(el: HTMLElement, ctx: Ctx, partnerId: string): Promise<void> {
  const club = ctx.club;
  if (!club || !lienOn(ctx) || !isBureau(ctx)) { mount(el, html``); return; }
  const [b, thread, follow] = await Promise.all([lienBoard(club.id), lienThread(partnerId).catch(() => [] as ThreadItem[]),
    lienFollowup(club.id).catch(() => [] as FollowInvite[])]);
  const p = b?.partners.find(x => x.id === partnerId);
  if (!b || !p) { mount(el, html``); return; }
  const again = () => renderLienSection(el, ctx, partnerId);
  const open = follow.find(i => i.partner_id === p.id) ?? null;
  const prop = proposalFor(b, p.id);
  const next = b.matches.filter(m => m.day >= b.today);
  const sug = open ? followCard(open, upperFirst(longDay(open.day)))
    : prop ? html`
      <div class="ln-sug" data-guide="ln-sug"><p class="eyebrow">À faire ce mois-ci</p>
        <p><strong>L’inviter au match ${prop.match.title}</strong><br><span class="muted small">${proposalLine(prop.match, b.gifts)}</span></p>
        <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" id="ln-prep" data-guide="ln-prepare">Préparer l’invitation</button>
          ${next.length > 1 ? html`<button type="button" class="btn btn-ghost btn-sm" id="ln-other">Un autre match</button>` : ''}</div></div>`
    : html`
      <div class="ln-sug ln-sug-calm"><p class="eyebrow">Ce mois-ci</p>
        <p class="muted small">${p.last ? 'Rien à faire : il a eu son attention récemment.' : 'Pas encore de geste : une invitation à un match, pour commencer ?'}</p>
        ${next.length ? html`<div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" id="ln-prep">Inviter à un match</button></div>` : ''}</div>`;
  mount(el, html`
    <section class="block ln-sec" data-guide="ln-fiche"><div class="block-head"><h3>Garder le lien</h3><span class="muted small">${lastGestureText(p.last?.on, b.today)}</span></div>
      ${sug}
      ${thread.length ? html`<ul class="ln-thread">${thread.map(t => html`
        <li><time>${dayMonth(t.on)}</time><span><strong>${t.title}</strong>${threadDetail(t) ? html`<br><span class="muted small">${threadDetail(t)}</span>` : ''}
          ${t.photo_id ? html`<br><button type="button" class="ln-link" data-photo="${t.photo_id}">Voir la photo</button>` : ''}</span></li>`)}</ul>`
        : html`<p class="muted small">Le fil des attentions commence à la première.</p>`}
      <button type="button" class="btn btn-ghost btn-sm ln-note-btn" id="ln-note">＋ Noter un geste</button>
    </section>
    <p class="hint">Rien d’argent dans ce fil : seulement des moments partagés.</p>`);
  $(el, '#ln-prep')?.addEventListener('click', () => void openInviteSheet(ctx, b, p, prop?.match.event_id ?? null, () => void again()));
  $(el, '#ln-other')?.addEventListener('click', () => {
    const other = next.find(m => m.event_id !== prop?.match.event_id) ?? null;
    void openInviteSheet(ctx, b, p, other?.event_id ?? null, () => void again());
  });
  if (open) wireFollow(el, [open], () => void again());
  for (const x of $$<HTMLButtonElement>(el, '[data-mark]')) x.addEventListener('click', async () => {
    try { await lienInviteMark(open!.id, x.dataset.mark === '1'); toast('Réponse notée'); await again(); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  const cancel = $<HTMLButtonElement>(el, '#ln-cancel');
  cancel?.addEventListener('click', async () => {
    if (!armed(cancel, 'Confirmer : annuler l’invitation')) return;
    try { await lienInviteCancel(open!.id); toast('Invitation annulée'); await again(); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  for (const x of $$<HTMLButtonElement>(el, '[data-photo]')) x.addEventListener('click', () => void showPhoto(x.dataset.photo!));
  $(el, '#ln-note')!.addEventListener('click', () => openNoteSheet(p, b.today, () => void again()));
}

async function showPhoto(id: string) {
  const d = await lienPhoto(id).catch(() => null);
  if (!d) { toast('Photo indisponible.', 'ko'); return; }
  openSheet('La photo', html`<img class="ln-big-photo" src="${jpeg(d)}" alt="Photo partagée avec le partenaire">`);
}

function openNoteSheet(p: BoardPartner, today: string, onDone: () => void) {
  openSheet(`Noter un geste · ${p.name}`, html`
    <form class="form" id="ln-nf" novalidate>
      <label class="field"><span>Quoi</span><select name="kind"><option value="geste">Un geste</option><option value="photo">Une photo</option>
        <option value="merci">Un merci</option><option value="voeux">Des vœux</option><option value="fin_saison">La fin de saison</option></select></label>
      <label class="field"><span>Le geste</span><input name="title" maxlength="120" required placeholder="Photo d’équipe signée"></label>
      <label class="field"><span>Un détail (facultatif)</span><input name="detail" maxlength="500" placeholder="Remise par les SF1 à la boutique"></label>
      <label class="field"><span>Le</span><input name="on" type="date" max="${today}" value="${today}"></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Noter dans le fil</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#ln-nf')!;
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      const msg = $(f, '.form-msg')!;
      if (!v('title')) { msg.textContent = 'Écris le geste.'; return; }
      if (hasMoney(`${v('title')} ${v('detail')}`)) { msg.textContent = 'Rien d’argent dans ce fil : seulement des moments partagés.'; return; }
      try { await lienNote(p.id, v('kind'), v('title'), v('detail') || null, v('on') || today); close(); toast('Noté dans le fil'); onDone(); }
      catch { msg.textContent = 'Impossible pour le moment.'; }
    });
  });
}

// =============================================================================================
// Les points d'entrée du bureau : la page Partenaires, l'accueil
// =============================================================================================
/** Sur la page Partenaires (bureau) : « Garder le lien », avec ce que l'app propose ce mois-ci. */
export async function renderLienEntry(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club || !lienOn(ctx) || !isBureau(ctx)) return;
  const b = await lienBoard(ctx.club.id).catch(() => null);
  if (!b) return;
  const items = monthTodo(b);   // le même compteur que l'accueil et « Garder le lien » (B4)
  mount(el, html`
    <a class="card card-row ln-entry" href="#/lien" data-guide="ln-entry">${ico('ti-heart')}
      <span class="card-main"><strong>Garder le lien</strong><span class="muted small">${items.length
        ? `${plural(items.length, 'attention', 'attentions')} ce mois-ci · un bon moment par trimestre, sans rien demander`
        : 'Un bon moment par trimestre pour chaque partenaire, sans rien demander'}</span></span>
      ${items.length ? html`<span class="chip chip-accent">${items.length}</span>` : html`<span class="occ-go" aria-hidden="true">›</span>`}</a>`);
}

/** L'accueil du bureau : une ligne quand l'app propose quelque chose. */
export async function renderLienHome(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club || !lienOn(ctx) || !isBureau(ctx)) return;
  const b = await lienBoard(ctx.club.id).catch(() => null);
  const items = b ? monthTodo(b) : [];
  if (!b || !items.length) return;
  const words = items.slice(0, 2).map(i => i.kind === 'invite' ? `inviter ${i.partner.name}` : i.kind === 'news' ? (i.draft ? 'les nouvelles à relire' : 'les nouvelles du trimestre')
    : i.kind === 'voeux' ? 'la carte de vœux' : i.kind === 'merci' ? 'un merci après la victoire' : 'la fin de saison');
  mount(el, html`
    <a class="card card-row ln-entry" href="#/lien">${ico('ti-heart')}
      <span class="card-main"><strong>Garder le lien · ${plural(items.length, 'attention', 'attentions')} ce mois-ci</strong>
        <span class="muted small">${upperFirst(andList(words))}${items.length > 2 ? '…' : ''}</span></span><span class="occ-go" aria-hidden="true">›</span></a>`);
}

// =============================================================================================
// Le soir du match : le merci au micro (table de marque), la photo avec la pancarte
// =============================================================================================
export async function renderMatchLien(el: HTMLElement, ctx: Ctx, eventId: string): Promise<void> {
  if (!lienOn(ctx)) return;
  const d = await lienMatch(eventId).catch(() => null);
  if (!d || (!d.lines.length && !d.photos.length)) { mount(el, html``); return; }
  mount(el, html`
    <section class="block ln-mic" data-guide="ln-direct">
      ${d.lines.length ? html`<div class="block-head"><h3>Pour la table de marque</h3><span class="chip chip-mute">au micro</span></div>
        <ul class="ln-lines">${d.lines.map(l => html`<li>« ${l} »</li>`)}</ul>
        <p class="hint">À lire à la mi-temps.</p>` : ''}
      ${d.photos.map(ph => html`
        <div class="ln-task"><span class="card-main"><strong>Photo avec ${ph.partner}</strong>
          <span class="muted small">Avec l’équipe et une pancarte « Merci ${ph.partner} »${ph.status === 'pas_cette_fois' ? ' · il ne vient pas, la photo reste possible' : ''}</span></span>
          ${ph.done ? html`<span class="chip chip-ok">Déposée</span>`
            : html`<label class="btn btn-ghost btn-sm">Déposer la photo<input type="file" accept="image/*" data-inv="${ph.invitation_id}" data-name="${ph.partner}" hidden></label>`}</div>`)}
    </section>`);
  for (const input of $$<HTMLInputElement>(el, 'input[data-inv]')) {
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      let data: string;
      try { data = await photoToJpeg(file); } catch { toast('Photo illisible.', 'ko'); return; }
      openPhotoSheet(input.dataset.inv!, input.dataset.name!, data, d.bureau, () => void renderMatchLien(el, ctx, eventId));
    });
  }
}

function openPhotoSheet(invitationId: string, partner: string, data: string, bureau: boolean, onDone: () => void) {
  openSheet(`La photo avec ${partner}`, html`
    <div class="form">
      <img class="ln-big-photo" src="${jpeg(data)}" alt="La photo à partager">
      <label class="check"><input type="checkbox" id="ln-ok"><span><strong>Droit à l’image</strong><br>
        <span class="muted small">Chaque personne visible est d’accord ; pour un mineur, ses parents ont donné leur accord photo au club.</span></span></label>
      ${bureau ? html`<label class="check"><input type="checkbox" id="ln-pub"><span><strong>Aussi sur la page du club</strong><br><span class="muted small">Avec les photos des matchs</span></span></label>` : ''}
      <button type="button" class="btn btn-primary" id="ln-share">Partager avec ${partner}</button>
      <p class="hint">Sans le droit à l’image, la photo reste au club : elle n’est ni partagée, ni publiée.</p>
      <p class="form-msg" role="alert"></p>
    </div>`, (sheet, close) => {
    const ok = $<HTMLInputElement>(sheet, '#ln-ok')!;
    const btn = $<HTMLButtonElement>(sheet, '#ln-share')!;
    const pub = $<HTMLInputElement>(sheet, '#ln-pub');
    const paint = () => { btn.textContent = ok.checked ? `Partager avec ${partner}` : 'Garder pour le club'; if (pub) pub.disabled = !ok.checked; };
    ok.addEventListener('change', paint);
    paint();
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try { await lienPhotoAdd(invitationId, data, ok.checked, !!pub?.checked && ok.checked); close(); toast(ok.checked ? `Photo partagée avec ${partner}` : 'Photo gardée pour le club'); onDone(); }
      catch (e) { btn.disabled = false; $(sheet, '.form-msg')!.textContent = String((e as Error).message).includes('semaine') ? 'Le jour du match ou la semaine qui suit.' : 'Envoi impossible.'; }
    });
  });
}

// =============================================================================================
// L'espace du partenaire : « Le club vous invite » (g3), au vouvoiement
// =============================================================================================
const invGifts = (i: SpaceInvitation) => i.gifts.filter(g => !g.is_bon && g.kind !== 'presente' && g.kind !== 'photo').map(g => shortOf(g));

function passCard(i: SpaceInvitation): Html {
  const title = bonTitle(i.gifts);
  const tonight = i.day === i.today;
  return html`
    <div class="ln-pass" data-guide="pa-bon">
      <p class="eyebrow">Le soir du match · à la buvette</p>
      <span class="ln-big">${title}</span>
      <span>Un bon offert par le club</span>
      ${i.bon_used_at ? html`<span class="ln-ring ln-used">Utilisé<br><small>code ${i.bon_code}</small></span>`
        : html`<button type="button" class="ln-ring" data-bon="${i.id}" ${tonight ? '' : 'disabled'}>${tonight ? 'Appuyez 5 s' : upperFirst(shortDay(i.day))}</button>`}
      <span class="small">${i.bon_used_at ? 'Bonne soirée !' : tonight ? 'Montrez l’écran à la buvette : un code et l’heure qui tourne.' : 'Valable le soir du match, une fois : un code et l’heure qui tourne, à montrer à la buvette.'}</span>
    </div>`;
}

export async function renderPartnerLien(root: HTMLElement, ctx: Ctx): Promise<void> {
  if (!lienOn(ctx)) return;
  const [s, mine] = await Promise.all([lienSpace().catch(() => null as Space | null), lienMyMail().catch(() => null)]);
  if (!s) { mount(root, html`<div class="empty"><p class="empty-title">Espace indisponible.</p></div>`); return; }
  // D-147 : l'invitation partie aussi par e-mail (une réponse suffit) et le mot laissé au club.
  const sent = new Map((await invitationMails(s.invitations.map(i => i.id))).map(x => [x.id, x]));
  void lienSeen().catch(() => undefined);   // les nouvelles et les attentions sont lues (« lues » dans le fil du bureau)
  const again = () => renderPartnerLien(root, ctx);
  const invs = s.invitations;
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${ctx.club?.name ?? ''}</p><h2>${invs.length ? 'Le club vous invite' : 'Le club et vous'}</h2></header>
      ${invs.map((i, k) => html`
        <section class="card ln-inv" ${k === 0 ? html`data-guide="pa-invite"` : ''}>${ico('ti-flag')}
          <span class="card-main"><strong>${i.title}</strong>
            <span class="muted small">${[upperFirst(longDay(i.day)), i.time, i.location].filter(Boolean).join(' · ')}</span>
            ${i.host_name ? html`<span class="muted small">${i.host_name} vous attendra à l’entrée.</span>` : ''}
            ${invGifts(i).length || i.presented || i.thanks_mic ? html`<span class="small">${upperFirst(andList([...invGifts(i), i.presented ? 'votre nom sur le match' : '',
              i.thanks_mic ? 'un merci au micro' : ''].filter(Boolean)))}.</span>` : ''}
            ${sent.get(i.id)?.mail_to ? html`<span class="muted small">Aussi envoyée par e-mail à ${sent.get(i.id)!.mail_to} : une réponse suffit, ici ou dans l’e-mail.</span>` : ''}
            ${i.status === 'vient' ? html`<span class="small ln-ok">Vous venez : le club est prévenu.</span>` : i.status === 'pas_cette_fois' ? html`<span class="small">Vous ne pouvez pas venir : le club est prévenu.</span>` : ''}
            <span class="btn-row"><button type="button" class="btn ${i.status === 'pas_cette_fois' ? 'btn-ghost' : 'btn-primary'} btn-sm" data-yes="${i.id}" aria-pressed="${String(i.status === 'vient')}">Je viens</button>
              <button type="button" class="btn ${i.status === 'pas_cette_fois' ? 'btn-primary' : 'btn-ghost'} btn-sm" data-no="${i.id}" aria-pressed="${String(i.status === 'pas_cette_fois')}">Je ne peux pas</button></span>
            ${i.status === 'pas_cette_fois' ? html`<form class="pm-word" data-word="${i.id}"><label class="field"><span>Un mot pour le club ? Facultatif</span>
              <textarea rows="2" maxlength="300">${sent.get(i.id)?.answer_note ?? ''}</textarea></label><button type="submit" class="btn btn-ghost btn-sm">Envoyer</button></form>` : ''}
          </span></section>
        ${i.gifts.some(g => g.is_bon) && i.status !== 'pas_cette_fois' ? passCard(i) : ''}`)}
      ${s.news.length ? html`<section class="block"><div class="block-head"><h3>Des nouvelles du club</h3></div>
        ${s.news.map(n => html`<article class="ln-news"><strong>${n.title}</strong><span class="muted small">${dayMonth(n.published_at.slice(0, 10))}</span>
          <p class="ln-body">${n.body}</p>
          ${n.photo_ids.length ? html`<div class="ln-photos">${n.photo_ids.map(id => html`<img alt="Photo du club" data-src="${id}">`)}</div>` : ''}</article>`)}</section>` : ''}
      ${s.gestures.length ? html`<section class="block"><div class="block-head"><h3>Les attentions du club</h3></div>
        <ul class="ln-thread">${s.gestures.map(g => html`<li><time>${dayMonth(g.on)}</time><span><strong>${g.title}</strong>${g.detail ? html`<br><span class="muted small">${g.detail}</span>` : ''}
          ${g.photo_id ? html`<br><img class="ln-thumb" alt="Photo partagée par le club" data-src="${g.photo_id}">` : ''}</span></li>`)}</ul></section>` : ''}
      ${!invs.length && !s.news.length && !s.gestures.length ? html`<div class="empty"><p class="empty-title">Le club vous donnera bientôt de ses nouvelles.</p>
        <p>Une invitation à un match, les nouvelles de chaque trimestre : elles arriveront ici.</p></div>` : ''}
      ${mine ? html`<section class="card pm-pref" data-guide="pa-mail-pref"><p class="eyebrow">Les messages du club</p>
        <p class="small">${mine.stopped ? 'Vous ne recevez plus les e-mails du club (« Ne plus recevoir ces messages ») : ses messages arrivent ici, avec une notification.'
          : mine.app_only || !mine.email ? 'Ses messages arrivent ici, avec une notification.'
          : html`Ses messages arrivent ici, avec une notification, et par e-mail à <strong>${mine.email}</strong>.`}</p>
        ${mine.email ? html`<label class="check"><input type="checkbox" id="pm-app-only" ${mine.app_only || mine.stopped ? 'checked' : ''}>
          <span><strong>Seulement dans l’app</strong><br><span class="muted small">Plus d’e-mail du club : la notification suffit</span></span></label>` : ''}</section>` : ''}
    </section>`);
  $<HTMLInputElement>(root, '#pm-app-only')?.addEventListener('change', async (e) => {
    const box = e.target as HTMLInputElement;
    try { await lienMailPref(box.checked); toast(box.checked ? 'Seulement dans l’app : plus d’e-mail du club' : 'Les messages du club arrivent aussi par e-mail'); }
    catch { box.checked = !box.checked; toast('Réglage impossible pour le moment.', 'ko'); }
  });
  for (const f of $$<HTMLFormElement>(root, '[data-word]')) f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const note = $<HTMLTextAreaElement>(f, 'textarea')!.value.trim();
    try { await lienReply(f.dataset.word!, false, note); toast(note ? 'Votre mot est envoyé au club' : 'C’est noté'); await again(); }
    catch { toast('Envoi impossible pour le moment.', 'ko'); }
  });
  for (const img of $$<HTMLImageElement>(root, 'img[data-src]')) void lienPhoto(img.dataset.src!).then(d => { if (d) img.src = jpeg(d); }).catch(() => undefined);
  const answer = async (b: HTMLButtonElement, id: string, yes: boolean) => {
    b.disabled = true;
    try { await lienReply(id, yes); toast(yes ? 'Merci : le club est prévenu' : 'Le club est prévenu'); await again(); }
    catch { b.disabled = false; toast('Réponse impossible pour le moment.', 'ko'); }
  };
  for (const b of $$<HTMLButtonElement>(root, '[data-yes]')) b.addEventListener('click', () => void answer(b, b.dataset.yes!, true));
  for (const b of $$<HTMLButtonElement>(root, '[data-no]')) b.addEventListener('click', () => void answer(b, b.dataset.no!, false));
  for (const b of $$<HTMLButtonElement>(root, '[data-bon]')) b.addEventListener('click', () => {
    const i = invs.find(x => x.id === b.dataset.bon)!;
    openUseHold(ctx, { title: bonTitle(i.gifts), picto: 'cadeau', details: `${i.title} · offert par le club` }, () => lienBonUse(i.id), () => void again());
  });
}

/** L'accueil du partenaire : l'invitation du club (ou ses nouvelles), en tête. */
export async function renderPartnerLienCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!lienOn(ctx)) return;
  const s = await lienSpace().catch(() => null);
  const i = s?.invitations[0];
  if (!s || (!i && !s.news.length)) return;
  mount(el, html`
    <a class="card card-row ln-entry" href="#/lien" data-guide="pa-lien">${ico(i ? 'ti-flag' : 'ti-megaphone')}
      <span class="card-main"><strong>${i ? 'Le club vous invite' : s.news[0]!.title}</strong>
        <span class="muted small">${i ? `${i.title} · ${longDay(i.day)}${i.status === 'vient' ? ' · vous venez' : i.status === 'envoyee' ? ' · votre réponse ?' : ''}` : 'Des nouvelles du club'}</span></span>
      <span class="occ-go" aria-hidden="true">›</span></a>`);
}

/** Les invitations du partenaire (son compte) : celles parties aussi par e-mail, et son mot (lecture de ses propres lignes). */
async function invitationMails(ids: string[]): Promise<{ id: string; mail_to: string | null; answer_note: string | null }[]> {
  const { supabase } = await import('../lib/supabase');
  if (!supabase || !ids.length) return [];
  const { data } = await supabase.from('lien_invitations').select('id, mail_to, answer_note').in('id', ids);
  return (data ?? []) as { id: string; mail_to: string | null; answer_note: string | null }[];
}
